import { supported as mapboxGlIsSupported } from '@mapbox/mapbox-gl-supported';
import classNames from 'classnames';
import { LngLatBounds, Map } from 'mapbox-gl';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { TAdChannelKey } from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import { EmptyState, TableStatsCard } from '@feathr/components';

import * as styles from './HeatmapCard.css';

interface ILocation {
  lat: number;
  lng: number;
  count: number;
}

interface IProps {
  /**
   * Active channel filter on Omni campaign reports. When `'meta'`, the
   * heatmap is replaced by an empty state because Meta does not expose
   * location-level data. `undefined` for Overview / non-Omni reports.
   */
  activeChannel?: TAdChannelKey;
  className?: string;
  /**
   * Force the Meta empty state regardless of `activeChannel`. Set on
   * Meta-only Pixl+ (Omni) campaigns: those reports never render channel
   * tabs (tabs require both Display + Meta integrations), so `activeChannel`
   * stays `undefined` even though the only data available is Meta's.
   */
  isMetaOnly?: boolean;
  points: ILocation[];
}

function getPointsBounds(points: ILocation[]): LngLatBounds {
  const intensityMax = getPointsMaxIntensity(points, 3);
  let sw: [number, number] = [Infinity, Infinity];
  let ne: [number, number] = [-Infinity, -Infinity];
  points.forEach((point) => {
    if (point.count < intensityMax) {
      return;
    }
    if (point.lng < sw[0]) {
      sw[0] = point.lng;
    }
    if (point.lat < sw[1]) {
      sw[1] = point.lat;
    }
    if (point.lng > ne[0]) {
      ne[0] = point.lng;
    }
    if (point.lat > ne[1]) {
      ne[1] = point.lat;
    }
  });
  if (sw[0] === Infinity || sw[1] === Infinity) {
    sw = [0, 50];
  }
  if (ne[0] === -Infinity || ne[1] === -Infinity) {
    ne = [0, -20];
  }
  return new LngLatBounds(sw, ne);
}

function getPointsMaxIntensity(points: ILocation[], deviations = 5): number {
  const counts = points.map((point) => point.count);
  const max = Math.max(...counts);
  const mean = counts.reduce((acc, count) => acc + count, 0) / counts.length;
  const sumOfSquaredDifferences = counts.reduce((acc, count) => acc + (count - mean) ** 2, 0);
  const standardDeviation = Math.sqrt(sumOfSquaredDifferences / (counts.length - 1));
  return Math.min(mean + standardDeviation * deviations, max);
}

function HeatmapCard({
  activeChannel,
  className,
  isMetaOnly,
  points,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const showMetaEmptyState = activeChannel === 'meta' || isMetaOnly;
  const mapContainer = useRef<HTMLDivElement | null>(null);
  /*
   * Single teardown path: the cleanup return covers both the channel
   * switch (effect re-runs when showMetaEmptyState flips) and full
   * unmount, so the mapbox-gl instance + its WebGL context never leak.
   */
  useEffect(() => {
    if (showMetaEmptyState || !mapContainer.current) {
      return undefined;
    }
    const intensityMax = getPointsMaxIntensity(points);
    const bounds = getPointsBounds(points);
    const map = new Map({
      container: mapContainer.current,
      style: 'mapbox://styles/alevental/ck1vf3f9j5h7d1cqp1j64zjkx?optimize=true',
      antialias: true,
      interactive: true,
      attributionControl: false,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: false,
      center: bounds.getCenter(),
      maxZoom: 10,
    });
    map.on('load', () => {
      map.addSource('locations', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: points.map((point) => ({
            type: 'Feature',
            properties: { count: point.count },
            geometry: {
              type: 'Point',
              coordinates: [point.lng, point.lat],
            },
          })),
        },
      });
      map.addLayer({
        id: 'locations-heatmap',
        type: 'heatmap',
        source: 'locations',
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'count'], 0, 0, intensityMax, 1],
          /*
           * Increase the heatmap color weight by zoom level
           * heatmap-intensity is a multiplier on top of heatmap-weight
           */
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 3],
          /*
           * Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
           * Begin color ramp at 0-stop with a 0-transparency color
           * to create a blur-like effect.
           */
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(87,169,227,0)',
            0.25,
            'rgb(87,169,227)',
            0.5,
            'rgb(38,189,179)',
            0.75,
            'rgb(254,230,77)',
            1,
            'rgb(246,146,30)',
          ],
          // Adjust the heatmap radius by zoom level
          'heatmap-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            ['interpolate', ['linear'], ['get', 'count'], 0, 4, intensityMax, 8],
            9,
            ['interpolate', ['linear'], ['get', 'count'], 0, 24, intensityMax, 80],
          ],
          // Adjust the heatmap opacity by zoom level
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0.8, 18, 0.8],
        },
      });
      map.fitBounds(bounds, { duration: 300, padding: 80 });
    });
    return (): void => {
      map.remove();
    };
  }, [showMetaEmptyState]);

  return (
    <TableStatsCard contentType={'bordered'} title={reportModuleLabels.includeHeatmap}>
      {showMetaEmptyState ? (
        <EmptyState
          description={t("Meta doesn't share location data")}
          label={t('No data available')}
          name={'heatmap_empty_slot'}
          theme={'neutral'}
          width={'full'}
        />
      ) : (
        <div className={styles.mapContainer}>
          {mapboxGlIsSupported() ? (
            <div className={classNames(styles.map, className)} ref={getMapboxRef} />
          ) : (
            <p>{t("Couldn't load heatmap. Try again.")}</p>
          )}
        </div>
      )}
    </TableStatsCard>
  );

  function getMapboxRef(el): HTMLDivElement | null {
    return (mapContainer.current = el);
  }
}

export default observer(HeatmapCard);
